import { ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { NgIf } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EMPTY, throwError } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { GoogleQRResponse, RecoveryCodesResponse, UserSettingsService } from '../../../../services/members/user-settings.service';
import { GrowlerService } from '../../../shared/growler/growler.service';
import { RecoveryCodesPanelComponent } from '../recovery-codes-panel/recovery-codes-panel.component';
import { isMethodEnrolled, isTwoFactorAlreadyEnabled, TWO_FACTOR_ALREADY_ENABLED_COPY } from './two-factor-already-enabled';
import { MembersService } from '../../../../services/members/members.service';

/** Authenticator-app enrollment step (QR, code, show-once recovery codes); the host renders `footer` in its own footer. */
@UntilDestroy()
@Component({
    selector: 'app-google-auth-setup-step',
    templateUrl: './google-auth-setup-step.component.html',
    styleUrls: ['./google-auth-setup-step.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgIf, RecoveryCodesPanelComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class GoogleAuthSetupStepComponent implements OnInit, OnDestroy
{
    /**
     * Label for the dismiss button. Defaults to 'Cancel' (the step is opened directly, so backing
     * out closes it). The gate dialog opens the step from a method chooser and passes 'Back' — the
     * dismiss returns to that chooser rather than closing the flow.
     */
    @Input() dismissLabel = 'Cancel';

    @Output() completed = new EventEmitter<void>();
    @Output() cancelled = new EventEmitter<void>();

    /**
     * Fires when the step reaches its show-once recovery-codes stage, so the host can swap its
     * header — the codes render inside the host's surface, not a dialog of their own.
     */
    @Output() showingRecoveryCodes = new EventEmitter<void>();

    @ViewChild('footer', {static: true}) footer: TemplateRef<unknown>;

    qrCode: SafeHtml | null = null;
    secretKey = '';
    code = '';
    isSaving = false;
    isSuccess = false;
    hasError = false;

    /** 'setup' takes the QR + 6-digit code; 'codes' is the show-once recovery-codes stage. */
    stage: 'setup' | 'codes' = 'setup';
    recoveryCodes: string[] = [];
    codesAcknowledged = false;

    private _errorTimer: ReturnType<typeof setTimeout> | null = null;
    private _successTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(
            private _userSettings: UserSettingsService,
            private _sanitizer: DomSanitizer,
            private _growler: GrowlerService,
            private _members: MembersService,
            private _cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        // Only the already-enrolled refusal is intercepted (SD-10440), and only once the server confirms
        // the authenticator is enrolled (review F-f7cf); anything else is rethrown so it reaches the
        // global error handler exactly as it did before this step had any error path.
        this._userSettings.getGoogleAuthQrCode().pipe(
            catchError((response: unknown) => {
                if (!isTwoFactorAlreadyEnabled(response)) {
                    return throwError(() => response);
                }
                return isMethodEnrolled(this._members, 'google').pipe(
                    switchMap((enrolled: boolean) => {
                        if (!enrolled) {
                            return throwError(() => response);
                        }
                        this._alreadyEnabled();
                        return EMPTY;
                    }),
                );
            }),
            untilDestroyed(this),
        ).subscribe({
            next: (response: GoogleQRResponse) => {
                this.qrCode = this._sanitizer.bypassSecurityTrustHtml(response.qr);
                this.secretKey = response.secret;
                this._cdr.markForCheck();
            },
        });
    }

    get verifyLabel(): string {
        if (this.isSuccess) {
            return 'Verified';
        }
        return this.isSaving ? 'Verifying…' : 'Verify';
    }

    onCodeChange(code: string): void {
        this.code = code ?? '';
    }

    verify(code: string): void {
        if (code.length !== 6 || this.isSaving || this.isSuccess) {
            return;
        }

        this.isSaving = true;
        this._cdr.markForCheck();

        this._userSettings.finishGoogleAuthSetup(code, this.secretKey).pipe(
            finalize(() => this.isSaving = false),
            untilDestroyed(this),
        ).subscribe({
            next: (response: RecoveryCodesResponse) => {
                this.isSuccess = true;
                this._cdr.markForCheck();
                this._growler.success('Success', 'The authenticator app has been enabled as a two-factor verification method on your account.');
                this._successTimer = setTimeout(() => this._finish(response?.recoveryCodes ?? []), 800);
            },
            error: (response: unknown) => {
                if (isTwoFactorAlreadyEnabled(response)) {
                    this._confirmAlreadyEnabled(() => this._triggerError());
                    return;
                }
                this._triggerError();
            },
        });
    }

    /**
     * Finish enrollment. When the server issued recovery codes (feature enabled, first method
     * enrolled), hand over to the show-once stage and hold completion until the user acknowledges
     * them; otherwise complete immediately.
     *
     * The codes render INSIDE this step rather than in their own dialog: enrollment is not done
     * until they are saved, and a separate dialog both stacked on the still-open host and let the
     * user into the app first.
     */
    private _finish(recoveryCodes: string[]): void {
        if (recoveryCodes.length) {
            this.recoveryCodes = recoveryCodes;
            this.stage = 'codes';
            this.showingRecoveryCodes.emit();
            this._cdr.markForCheck();
            return;
        }
        this.completed.emit();
    }

    /** Show-once stage acknowledged — enrollment is complete. */
    onCodesAcknowledged(): void {
        if (!this.codesAcknowledged) {
            return;
        }
        this.completed.emit();
    }

    onBack(): void {
        this.cancelled.emit();
    }

    ngOnDestroy(): void {
        if (this._errorTimer) { clearTimeout(this._errorTimer); }
        if (this._successTimer) { clearTimeout(this._successTimer); }
    }

    /**
     * Core said the authenticator app is already enabled (SD-10440). Close only if the server confirms
     * it is enrolled; otherwise fall back to the original error (review F-f7cf).
     */
    private _confirmAlreadyEnabled(otherwise: () => void): void {
        isMethodEnrolled(this._members, 'google').pipe(untilDestroyed(this)).subscribe((enrolled: boolean) => {
            if (enrolled) {
                this._alreadyEnabled();
            } else {
                otherwise();
            }
        });
    }

    /**
     * The server confirmed the authenticator app is enrolled (SD-10440): a double-submit, a retry, or
     * a stale second tab. Nothing needs fixing, so say so calmly and close as completed — both hosts
     * (Settings and the login gate) read that as enrolled.
     */
    private _alreadyEnabled(): void {
        this._growler.success(TWO_FACTOR_ALREADY_ENABLED_COPY.title, TWO_FACTOR_ALREADY_ENABLED_COPY.message);
        this.completed.emit();
    }

    private _triggerError(): void {
        this.hasError = true;
        this._cdr.markForCheck();
        this._growler.error('Error', 'We were unable to verify that code. Please try again.');
        this._errorTimer = setTimeout(() => {
            this.hasError = false;
            this.code = '';
            this._cdr.markForCheck();
        }, 600);
    }
}
